import type { SharePriceCellData } from './types';
import { kind } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { isCurrencyISO } from '@/constants/currencies';

import { currencyToSymbol } from '@/utilities/formatting';
import { MINIMUM_SUPPORTED_NUM } from '../constants';

const getDefaultCellData = ({
  value = null,
  currencyISO = null,
  error = null,
}: Partial<SharePriceCellData> = {}): SharePriceCellData => ({
  kind,
  value,
  error,
  currencyISO,
});

const isCellValue = (value: unknown): value is SharePriceCellData['value'] => {
  return typeof value === 'number' || value === null;
};

const isCellData = (data: unknown): data is SharePriceCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

type Args = Pick<Partial<SharePriceCellData>, 'currencyISO' | 'error'> & {
  value?: unknown;
};

/**
 * ## getCellTemplate
 *
 * @param value The value of the SharePriceCell
 * @param currencyISO The currencyISO to format the SharePriceCell value
 * @returns A custom SharePriceCell object and its data
 */
export const getCellTemplate = ({
  value = null,
  currencyISO = null,
  error = null,
}: Args = {}) => {
  if (isCellValue(value)) {
    const validCurrencyISO = isCurrencyISO(currencyISO) ? currencyISO : null;

    const currencySymbol: string | undefined =
      currencyToSymbol[currencyISO ?? '']?.currency.symbol;

    // `copyData` is used as the accessible text for the cell
    // If the cell is copied, `copyData` is passed to the devices clipboard
    // In this case we want to copy the currency symbol and the value unformatted
    // We do not fully format the value because we want to keep it as a recognisable dot decimal number
    // When pasting between cells, the `copyData` can be split back into it currencyISO and numberic parts
    const copyData = `${currencySymbol ?? ''}${String(value ?? '')}`;

    const cellData = getDefaultCellData({
      value,
      currencyISO: validCurrencyISO,
      error,
    });
    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultCellData() });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data?: unknown) => {
  if (isCellData(data) && isCellValue(data.value)) {
    return data;
  }

  return getDefaultCellData();
};

// This function is used to sanitise the pasted value by removing any non-numeric characters
const sanitiseValue = (value: string): string => {
  return value.split(/(?!-)[^0-9.]/).filter((n) => n.length)[0] ?? '';
};

const getDotDecimalValue = (value: string) => value.replace(/,/g, '.');

export const validateSharePrice = (price: number | null) => {
  if (price === null) return null; // allow null values

  if (price === 0) {
    return "Price can't be 0";
  }

  if (Math.abs(price) < MINIMUM_SUPPORTED_NUM) {
    return 'Max 9 decimals';
  }

  return null;
};

export const validatePastedValue = (
  pastedValue: string,
  cell: SharePriceCellData
): SharePriceCellData => {
  // If you paste a value with 1 comma AND 1 dot (eg. 1,000.89 or 1.000,90), then throw an error
  if (pastedValue.includes(',') && pastedValue.includes('.')) {
    return { ...cell, error: 'No commas, only dots' };
  }

  // If you paste a value with a comma (eg. 10,98), then convert to dot (10.98)
  // If you paste a value with multiple comma (eg. 10,005,000.50), this converts to (10.005)
  const dotDecimalValue = getDotDecimalValue(pastedValue);

  const value = Number.parseFloat(sanitiseValue(dotDecimalValue));

  if (Number.isNaN(value)) {
    return { ...cell, value: null, error: 'Enter numbers only' };
  }

  const error = validateSharePrice(Math.abs(value));

  if (error) {
    return { ...cell, value: null, error };
  }

  return {
    ...cell,
    value: Math.abs(value),
    error: null,
  };
};
